import { computed, inject, Injectable, signal } from '@angular/core';
import { PendingTasks } from '@angular/core';
import { BrowserTabDefaults } from '../models/entities/browser-tab-defaults.model';
import {
  eventIdFromProcessingRoutePath,
  formatTabKey,
  nextTabNumberFromKeys,
  parseAgentTabRoute,
  TAB_EVENTS_PAGE,
} from '../routing/browser-tab-routes';
import {
  BROWSER_TAB_MAX_COUNT,
  BrowserTabDisplayMeta,
  BrowserTabPageState,
  BrowserTabPopoutPayload,
  createDefaultBrowserTabPageState,
} from '../models/browser-tab.models';
import { isDealerEventUuid, parseDealerSiteId } from '../utils/dealer-event-id.util';
import { EventDataCacheService } from './event-data-cache.service';

/**
 * Owns browser chrome (tab list, active tab) and isolated page state per tab.
 * See `.cursor/rules/angular-browser-tabs.mdc` before adding tab-scoped behavior.
 */
@Injectable({ providedIn: 'root' })
export class BrowserTabService {
  private readonly eventDataCache = inject(EventDataCacheService);
  private readonly pendingTasks = inject(PendingTasks);
  private nextTabNumber = 2;
  private readonly tabDefaults = signal<BrowserTabDefaults | null>(null);

  readonly tabs = signal<BrowserTabDisplayMeta[]>([
    { id: 't1', label: 'New Event', instanceId: crypto.randomUUID() },
  ]);
  readonly activeTabId = signal('t1');
  readonly isPopoutWindow = signal(false);
  /** Tabs whose landing claim is in flight. Other tabs stay interactive. */
  private readonly claimLockedTabIds = signal<ReadonlySet<string>>(new Set());

  private readonly tabPageStates = signal<Record<string, BrowserTabPageState>>({
    t1: createDefaultBrowserTabPageState(),
  });

  readonly activePageState = computed(
    () => this.tabPageStates()[this.activeTabId()] ?? createDefaultBrowserTabPageState(),
  );
  readonly tabDetails = computed(() =>
    this.tabs().map((tab) => ({
      tab,
      pageState: this.getPageState(tab.id),
    })),
  );
  readonly pageState = computed(() => ({
    activeEventId: this.activeEventId(),
    events: this.tabDetails()
      .filter((t) => t.pageState.activeEventId)
      .map((t) => ({
        id: t.pageState.activeEventId!,
        eventProcessingMode: t.pageState.eventProcessingMode,
        label: t.tab.label,
        /** Stable cross-app tab identity for timer + workspace correlation. */
        correlationId: t.tab.instanceId,
        /** Numeric Dealer site id so peers can restore cameras / talkdowns. */
        siteId:
          t.pageState.activeEventSiteId != null && t.pageState.activeEventSiteId > 0
            ? t.pageState.activeEventSiteId
            : (parseDealerSiteId(t.pageState.pendingAction?.siteId) ?? undefined),
      })),
  }));

  /** True when the active tab is waiting on a landing-page claim. */
  readonly claimInteractionLocked = computed(() => {
    const tabId = this.activeTabId();
    return !!tabId && this.claimLockedTabIds().has(tabId);
  });
  readonly expanded = computed(() => this.activePageState().expanded);
  readonly showEventQueue = computed(() => this.activePageState().showEventQueue);
  readonly selectedEventId = computed(() => this.activePageState().selectedEventId);
  readonly eventsSort = computed(() => this.activePageState().eventsSort);
  readonly patrolSort = computed(() => this.activePageState().patrolSort);
  readonly inProcessSort = computed(() => this.activePageState().inProcessSort);
  readonly parkedSort = computed(() => this.activePageState().parkedSort);
  readonly eventsActionPopover = computed(() => this.activePageState().eventsActionPopover);
  readonly eventsActionSelectedSiteId = computed(
    () => this.activePageState().eventsActionSelectedSiteId,
  );
  readonly eventsActionSearchQuery = computed(() => this.activePageState().eventsActionSearchQuery);
  readonly eventsActionOccupancySiteIds = computed(
    () => this.activePageState().eventsActionOccupancySiteIds,
  );
  readonly sitesStatusListPopover = computed(() => this.activePageState().sitesStatusListPopover);
  readonly sitesStatusListSearchQuery = computed(
    () => this.activePageState().sitesStatusListSearchQuery,
  );
  readonly siteArmDisarmPopover = computed(() => this.activePageState().siteArmDisarmPopover);
  readonly siteArmDisarmSiteId = computed(() => this.activePageState().siteArmDisarmSiteId);
  readonly siteDisarmReasonId = computed(() => this.activePageState().siteDisarmReasonId);
  readonly siteDisarmFor = computed(() => this.activePageState().siteDisarmFor);
  readonly siteDisarmFixedDurationMinutes = computed(
    () => this.activePageState().siteDisarmFixedDurationMinutes,
  );
  readonly siteDisarmFixedDurationCustom = computed(
    () => this.activePageState().siteDisarmFixedDurationCustom,
  );
  readonly siteDisarmFixedDurationCustomDraft = computed(
    () => this.activePageState().siteDisarmFixedDurationCustomDraft,
  );
  readonly siteDisarmUntilAt = computed(() => this.activePageState().siteDisarmUntilAt);
  readonly pendingAction = computed(() => this.activePageState().pendingAction);
  readonly activeEventId = computed(() => this.activePageState().activeEventId);
  readonly eventViewTab = computed(() => this.activePageState().eventViewTab);
  readonly sidePanel = computed(() => this.activePageState().sidePanel);
  readonly alarmFilters = computed(() => this.activePageState().alarmFilters);
  readonly alarmFlagOverrides = computed(() => this.activePageState().alarmFlagOverrides);
  readonly eventViewPopover = computed(() => this.activePageState().eventViewPopover);
  readonly eventViewPopoverSearchQuery = computed(
    () => this.activePageState().eventViewPopoverSearchQuery,
  );
  readonly eventViewPopoverPriorities = computed(
    () => this.activePageState().eventViewPopoverPriorities,
  );
  readonly eventViewPopoverSelectedOutcomeId = computed(
    () => this.activePageState().eventViewPopoverSelectedOutcomeId,
  );
  readonly eventViewPopoverClosingDetails = computed(
    () => this.activePageState().eventViewPopoverClosingDetails,
  );
  readonly eventViewPopoverParkDurationMinutes = computed(
    () => this.activePageState().eventViewPopoverParkDurationMinutes,
  );

  /** Stores bootstrap defaults without resetting open tabs. */
  setTabDefaults(defaults: BrowserTabDefaults): void {
    this.tabDefaults.set(defaults);
  }

  /** Drops leftover mock event ids so live Dealer mode never opens a stale tab. */
  sanitizeForLiveDealerMode(): void {
    const tabIds = this.tabs().map((tab) => tab.id);

    for (const tabId of tabIds) {
      const state = this.getPageState(tabId);
      const routeEventId = eventIdFromProcessingRoutePath(state.routePath);
      const staleActive = !!state.activeEventId && !isDealerEventUuid(state.activeEventId);
      const staleRoute = !!routeEventId && !isDealerEventUuid(routeEventId);

      if (!staleActive && !staleRoute) {
        continue;
      }

      this.eventDataCache.clearEvent(state.activeEventId);
      this.patchTabState(tabId, {
        routePath: TAB_EVENTS_PAGE,
        activeEventId: null,
        activeEventSiteId: null,
        eventHandleId: null,
        claimedAt: null,
        pendingAction: null,
        eventProcessingMode: 'process-event',
        eventViewPopover: null,
      });
    }
  }

  /** Aligns active tab and route path with the current browser URL after reload. */
  syncActiveTabFromUrl(url: string): void {
    const parsed = parseAgentTabRoute(url);
    if (!parsed) {
      return;
    }

    if (!this.hasTab(parsed.tabId)) {
      return;
    }

    this.selectTab(parsed.tabId);
    this.updateTabRoutePath(parsed.tabId, parsed.routePath);
  }

  /** Update tab chrome label and optional tag chips shown in hover tooltips. */
  updateTabDisplay(tabId: string, display: { label?: string; tags?: string[] }): void {
    this.tabs.update((tabs) =>
      tabs.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              ...(display.label !== undefined ? { label: display.label } : {}),
              ...(display.tags !== undefined ? { tags: display.tags } : {}),
            }
          : tab,
      ),
    );
  }

  /** Update the display label for a browser tab (e.g. location name after loading an event). */
  updateTabLabel(tabId: string, label: string): void {
    this.updateTabDisplay(tabId, { label });
  }

  /** Open work tabs (active entity) — empty chrome does not count. */
  openWorkCount(): number {
    return this.tabDetails().filter((t) => !!t.pageState.activeEventId).length;
  }

  emptyChromeCount(): number {
    return this.tabDetails().filter((t) => !t.pageState.activeEventId).length;
  }

  /**
   * Plus / New Event: under work cap and at most one empty chrome tab.
   */
  canAddTab(): boolean {
    return this.openWorkCount() < BROWSER_TAB_MAX_COUNT && this.emptyChromeCount() < 1;
  }

  /** True when the user may start another claim/patrol (work slots remaining). */
  canOpenMoreWork(): boolean {
    return this.openWorkCount() < BROWSER_TAB_MAX_COUNT;
  }

  selectTab(id: string): void {
    if (!this.tabs().some((tab) => tab.id === id)) {
      return;
    }

    if (this.activeTabId() === id) {
      return;
    }

    this.ensureTabState(id);
    this.activeTabId.set(id);
  }

  addTab(): string | null {
    if (!this.canAddTab()) {
      return null;
    }

    const id = formatTabKey(this.nextTabNumber++);
    const instanceId = crypto.randomUUID();

    const label = this.tabDefaults()?.defaultTabLabel ?? 'New Event';
    this.tabs.update((tabs) => [...tabs, { id, label, instanceId }]);
    this.ensureTabState(id);
    this.activeTabId.set(id);
    return id;
  }

  removeTab(id: string, options?: { releaseRegistry?: boolean }): void {
    if (this.tabs().length <= 1) {
      return;
    }

    this.eventDataCache.clearEvent(this.getPageState(id).activeEventId);
    this.setClaimInteractionLocked(id, false);
    this.tabs.update((tabs) => tabs.filter((tab) => tab.id !== id));

    this.tabPageStates.update((states) => {
      const { [id]: _removed, ...remaining } = states;
      return remaining;
    });

    if (this.activeTabId() === id) {
      const remainingTabs = this.tabs();
      this.activeTabId.set(remainingTabs[remainingTabs.length - 1]?.id ?? '');
      if (this.activeTabId()) {
        this.ensureTabState(this.activeTabId());
      }
    }
  }

  /**
   * Marks the active tab as transferring to a popout window so it still counts
   * toward the per-user max while the child window claims it.
   */
  beginPopoutTransfer(popoutId: string): string | null {
    const tab = this.tabs().find((item) => item.id === this.activeTabId());
    if (!tab?.instanceId) {
      return null;
    }
    return tab.instanceId;
  }

  toggleExpanded(): void {
    this.updateActivePageState({ expanded: !this.activePageState().expanded });
  }

  /** Restores a single tab workspace opened via expand-to-new-window. */
  hydrateFromPopout(payload: BrowserTabPopoutPayload): void {
    const parsedRoute = parseAgentTabRoute(payload.routeUrl);
    const routePath = parsedRoute?.routePath ?? payload.pageState.routePath ?? 'events';
    const instanceId = payload.tab.instanceId ?? crypto.randomUUID();
    const tab: BrowserTabDisplayMeta = {
      ...payload.tab,
      instanceId,
    };

    this.isPopoutWindow.set(true);
    this.nextTabNumber = nextTabNumberFromKeys([tab.id]);
    this.tabs.set([tab]);
    this.activeTabId.set(tab.id);
    this.tabPageStates.set({
      [tab.id]: {
        ...payload.pageState,
        routePath,
        // Keep full page chrome in the new window (not in-window "expanded" mode).
        expanded: false,
      },
    });
  }

  /** Sole write path for tab-scoped UI state on the active tab. */
  updateActivePageState(update: Partial<BrowserTabPageState>): void {
    this.updatePageState(this.activeTabId(), update);
  }

  /** Update a known tab after asynchronous work started while it was active. */
  updatePageState(tabId: string, update: Partial<BrowserTabPageState>): void {
    if (!this.hasTab(tabId)) {
      return;
    }
    this.patchTabState(tabId, update);
  }

  /** Replace full page state for the active tab (use sparingly — prefer partial updates). */
  setActivePageState(state: BrowserTabPageState): void {
    this.patchTabState(this.activeTabId(), state);
  }

  /** Apply page defaults loaded from mock JSON or future API bootstrap. */
  applyDefaults(defaults: BrowserTabDefaults): void {
    this.tabDefaults.set(defaults);
    this.reset();
  }

  /**
   * Blocks landing and shell interactions on one tab until its claim UI settles.
   * The returned function clears the lock and releases the pending task.
   */
  beginClaimInteraction(tabId: string): () => void {
    this.setClaimInteractionLocked(tabId, true);
    const done = this.pendingTasks.add();
    let ended = false;
    return () => {
      if (ended) {
        return;
      }
      ended = true;
      this.setClaimInteractionLocked(tabId, false);
      done();
    };
  }

  /** Blocks landing and shell interactions on one tab until its claim UI settles. */
  setClaimInteractionLocked(tabId: string, locked: boolean): void {
    if (!tabId) {
      return;
    }

    this.claimLockedTabIds.update((ids) => {
      if (ids.has(tabId) === locked) {
        return ids;
      }
      const next = new Set(ids);
      if (locked) {
        next.add(tabId);
      } else {
        next.delete(tabId);
      }
      return next;
    });
  }

  /**
   * @param options.empty When true, clear every tab instead of seeding New Event.
   */
  reset(options?: { empty?: boolean }): void {
    this.isPopoutWindow.set(false);
    this.claimLockedTabIds.set(new Set());
    this.eventDataCache.clear();
    if (options?.empty) {
      this.nextTabNumber = 1;
      this.tabs.set([]);
      this.activeTabId.set('');
      this.tabPageStates.set({});
      return;
    }

    const defaults = this.tabDefaults();
    const initialTabs = (defaults?.defaultTabs ?? [{ id: 't1', label: 'New Event' }]).map(
      (tab) => ({
        ...tab,
        instanceId: crypto.randomUUID(),
      }),
    );

    this.nextTabNumber = nextTabNumberFromKeys(initialTabs.map((tab) => tab.id));
    this.tabs.set(initialTabs);
    this.activeTabId.set(initialTabs[0]?.id ?? 't1');
    this.tabPageStates.set(
      Object.fromEntries(initialTabs.map((tab) => [tab.id, createDefaultBrowserTabPageState()])),
    );
  }

  /**
   * Resets this window to a fresh default tab after the last local tab was popped out.
   * Does not clear other windows' registry entries.
   */
  resetAfterLastTabPopout(): void {
    this.isPopoutWindow.set(false);
    this.eventDataCache.clear();
    const defaults = this.tabDefaults();
    const initialTabs = (defaults?.defaultTabs ?? [{ id: 't1', label: 'New Event' }]).map(
      (tab) => ({
        ...tab,
        instanceId: crypto.randomUUID(),
      }),
    );

    this.nextTabNumber = nextTabNumberFromKeys(initialTabs.map((tab) => tab.id));
    this.tabs.set(initialTabs);
    this.activeTabId.set(initialTabs[0]?.id ?? 't1');
    this.tabPageStates.set(
      Object.fromEntries(initialTabs.map((tab) => [tab.id, createDefaultBrowserTabPageState()])),
    );
  }

  getPageState(tabId: string): BrowserTabPageState {
    return this.tabPageStates()[tabId] ?? createDefaultBrowserTabPageState();
  }

  hasTab(tabId: string): boolean {
    return this.tabs().some((tab) => tab.id === tabId);
  }

  findTab(eventId: string): BrowserTabDisplayMeta | null {
    const tabs = this.tabs();
    const tabStates = tabs.map(({ id }) => this.getPageState(id));
    const existingTabId = tabStates.findIndex((pageState) => pageState.activeEventId === eventId);
    if (existingTabId >= 0) return tabs[existingTabId];
    return null;
  }

  getTab(tabId: string): BrowserTabDisplayMeta | null {
    return this.tabs().find((tab) => tab.id === tabId) || null;
  }

  /**
   * Another tab in this window that still holds an unresolved event
   * (used after raise/close when auto-process is off).
   */
  findOtherTabWithActiveEvent(excludeTabId?: string): { tabId: string; eventId: string } | null {
    const skipId = excludeTabId ?? this.activeTabId();
    for (const tab of this.tabs()) {
      if (tab.id === skipId) {
        continue;
      }

      const eventId = this.getPageState(tab.id).activeEventId;
      if (eventId) {
        return { tabId: tab.id, eventId };
      }
    }

    return null;
  }

  updateTabRoutePath(tabId: string, routePath: string): void {
    this.patchTabState(tabId, { routePath });
  }

  private ensureTabsHaveInstanceIds(): void {
    this.tabs.update((tabs) =>
      tabs.map((tab) => (tab.instanceId ? tab : { ...tab, instanceId: crypto.randomUUID() })),
    );
  }

  private ensureTabState(tabId: string): void {
    this.tabPageStates.update((states) => {
      if (states[tabId]) {
        return states;
      }

      return { ...states, [tabId]: createDefaultBrowserTabPageState() };
    });
  }

  private patchTabState(tabId: string, update: Partial<BrowserTabPageState>): void {
    this.tabPageStates.update((states) => {
      const current = states[tabId] ?? createDefaultBrowserTabPageState();
      const nextEventId = update.activeEventId;
      // A different event starts on Alarms. The same event, and any write that
      // chooses a tab itself, keeps Live Cameras or Playback.
      const openingAnotherEvent =
        typeof nextEventId === 'string' &&
        nextEventId.length > 0 &&
        nextEventId !== current.activeEventId &&
        update.eventViewTab === undefined;

      return {
        ...states,
        [tabId]: {
          ...current,
          ...update,
          ...(openingAnotherEvent ? { eventViewTab: 'alarms' as const } : {}),
        },
      };
    });
  }
}
